'use client';

import * as React from 'react';
import { buttonVariants, P } from '@paypoteam/ui-components';
import { CaretRight } from '@phosphor-icons/react';
import { cva, type VariantProps } from 'class-variance-authority';
import Link from 'next/link';

import { cn } from '@/lib/utils';

import { LocalImage } from '@/components/atoms/LocalImage';
import H2 from '@/components/atoms/Typography/H2';
import Section from '@/components/layout/Section';

import type { StatusSectionConfigType } from './StatusSection.types';

export const statusSectionVariants = cva('', {
    variants: {},
    defaultVariants: {},
});

export interface StatusSectionProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof statusSectionVariants> {
    config: StatusSectionConfigType;
}

const StatusSection = React.forwardRef<HTMLDivElement, StatusSectionProps>(
    ({ config, className, ...props }, ref) => (
        <Section
            className={cn(statusSectionVariants({ className }))}
            containerClassName="flex flex-col items-center"
            ref={ref}
            {...props}
        >
            <LocalImage
                width={config.image.width || 230}
                height={config.image.height || 230}
                className="mb-12 object-contain md:h-[300px] md:w-[300px]"
                {...config.image}
            />

            <H2
                className={cn(
                    'text-center',
                    !!config.description && 'heading-spaces',
                )}
            >
                {config.title}
            </H2>

            {config.description ? (
                <P className="text-center">{config.description}</P>
            ) : null}

            {config.link ? (
                <Link
                    href={config.link.href}
                    className={buttonVariants({
                        variant: 'outline',
                        className: 'mt-8 w-full sm:w-fit md:mt-10',
                    })}
                >
                    {config.link.title}

                    <CaretRight
                        weight="bold"
                        className="ml-1"
                    />
                </Link>
            ) : null}
        </Section>
    ),
);

export default StatusSection;
